import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import api from '../utils/api';
import { GroupedOfficeMenu } from './GroupedOfficeMenu';
import { getCachedLogo, cacheLogo } from '../utils/logo';

/**
 * サイト共通ヘッダー：ロゴ + ナビゲーション（サブメニュー対応）
 * フロントページと他ページ全体で見た目を統一するためのコンポーネント
 */
const SiteHeader = ({ activePath }) => {
  const [navigationItems, setNavigationItems] = useState([]);
  const [offices, setOffices] = useState([]);
  const [companyInfo, setCompanyInfo] = useState(null);
  const [groupOrder, setGroupOrder] = useState([]);
  const [expandedMenu, setExpandedMenu] = useState(null);

  useEffect(() => {
    api.get('/navigation').then((res) => setNavigationItems(res.data)).catch(() => {});
    api.get('/offices/active').then((res) => setOffices(res.data)).catch(() => {});
    api.get('/company-info').then((res) => { setCompanyInfo(res.data); cacheLogo(res.data?.logo_url); }).catch(() => {});
    api.get('/offices/group-order').then((res) => setGroupOrder(res.data.order || [])).catch(() => {});
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (expandedMenu && !event.target.closest('nav')) {
        setExpandedMenu(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [expandedMenu]);

  return (
    <header className="fixed top-0 left-0 right-0 z-50 backdrop-blur-md bg-white/80 border-b border-amber-500/20" data-testid="site-header">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* ロゴ */}
        <div className="flex justify-between items-center py-3 sm:py-4 gap-2">
          <Link to="/" onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })} className="flex items-center space-x-2 sm:space-x-3 min-w-0">
            <div className="relative flex-shrink-0 h-[51px] sm:h-[72px] flex items-center">
              <img
                src={companyInfo?.logo_url || getCachedLogo()}
                alt="Presage Corporation"
                className="h-full w-auto object-contain"
                data-testid="site-header-logo-img"
              />
            </div>
            <div className="flex flex-col justify-center min-w-0">
              <span className="text-sm sm:text-xl font-bold text-gray-900 truncate leading-tight">
                Presage Corporation
              </span>
              <span className="text-[10px] sm:text-xs text-amber-500 truncate leading-tight">株式会社プレサージュ</span>
            </div>
          </Link>
        </div>

        {/* ナビゲーション */}
        <nav className="border-t border-b border-amber-500/20 py-[11px] sm:py-[14px]" data-testid="main-nav">
          <ul className="flex flex-wrap justify-center items-center gap-x-4 gap-y-2 sm:gap-x-6 md:gap-x-8 px-2">
            {navigationItems
              .filter((item) => !item.parent_id && item.path !== '/privacy')
              .map((item) => {
                const subItems = navigationItems.filter((sub) => sub.parent_id === item.id);
                const isOfficesMenu = item.path === '/operating-office' && offices.length > 0;
                const hasSubMenu = subItems.length > 0 || isOfficesMenu;
                const isExpanded = expandedMenu === item.id;
                const isActive = item.path === activePath || subItems.some((sub) => sub.path === activePath);

                return (
                  <li key={item.path} className="relative">
                    {hasSubMenu ? (
                      <button
                        onClick={() => setExpandedMenu(isExpanded ? null : item.id)}
                        className={`text-sm transition-colors duration-300 relative group flex items-center ${
                          isActive ? 'text-amber-500' : 'text-gray-600 hover:text-amber-500'
                        }`}
                        data-testid={isOfficesMenu ? 'nav-offices-button' : undefined}
                      >
                        {item.label}
                        <span className="ml-1 transition-transform duration-300" style={{ transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)' }}>▼</span>
                        <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-gradient-to-r from-amber-500 to-amber-600 group-hover:w-full transition-all duration-300"></span>
                      </button>
                    ) : (
                      <Link
                        to={item.path}
                        className={`text-sm transition-colors duration-300 relative group ${
                          isActive ? 'text-amber-500' : 'text-gray-600 hover:text-amber-500'
                        }`}
                      >
                        {item.label}
                        <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-gradient-to-r from-amber-500 to-amber-600 group-hover:w-full transition-all duration-300"></span>
                      </Link>
                    )}

                    {/* 運営事業所：トップページの各事業所へ遷移 */}
                    {isOfficesMenu && isExpanded && (
                      <GroupedOfficeMenu offices={offices} groupOrder={groupOrder} onNavigate={() => setExpandedMenu(null)} />
                    )}

                    {!isOfficesMenu && hasSubMenu && isExpanded && (
                      <ul className="absolute left-0 top-full mt-2 w-56 bg-white/95 backdrop-blur-md border border-amber-500/30 rounded-lg shadow-lg z-50">
                        {subItems.map((subItem) => (
                          <li key={subItem.path}>
                            <Link
                              to={subItem.path}
                              onClick={() => setExpandedMenu(null)}
                              className={`flex items-center gap-3 px-4 py-2 text-sm transition-colors first:rounded-t-lg last:rounded-b-lg ${
                                subItem.path === activePath ? 'text-amber-500 bg-amber-500/20' : 'text-gray-600 hover:text-amber-500 hover:bg-amber-500/10'
                              }`}
                            >
                              {subItem.image_url && (
                                <img
                                  src={subItem.image_url}
                                  alt={subItem.label}
                                  className="w-8 h-8 rounded-full object-cover border border-amber-500/30"
                                  onError={(e) => { e.target.style.display = 'none'; }}
                                />
                              )}
                              <span>{subItem.label}</span>
                            </Link>
                          </li>
                        ))}
                      </ul>
                    )}
                  </li>
                );
              })}

            {/* 運営事業所ドロップダウン（ナビに該当項目が無い場合のみ追加） */}
            {offices.length > 0 && !navigationItems.some((i) => !i.parent_id && i.path === '/operating-office') && (
              <li className="relative">
                <button
                  onClick={() => setExpandedMenu(expandedMenu === 'offices' ? null : 'offices')}
                  className="text-sm text-gray-600 hover:text-amber-500 transition-colors duration-300 relative group flex items-center"
                  data-testid="nav-offices-button"
                >
                  事業内容
                  <span className="ml-1 transition-transform duration-300" style={{ transform: expandedMenu === 'offices' ? 'rotate(180deg)' : 'rotate(0deg)' }}>▼</span>
                  <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-gradient-to-r from-amber-500 to-amber-600 group-hover:w-full transition-all duration-300"></span>
                </button>
                {expandedMenu === 'offices' && (
                  <GroupedOfficeMenu offices={offices} groupOrder={groupOrder} onNavigate={() => setExpandedMenu(null)} />
                )}
              </li>
            )}
          </ul>
        </nav>
      </div>
    </header>
  );
};

export default SiteHeader;
